/**
 * `@shn/envelope-crypto` — substrate envelope-encryption primitives.
 *
 * **Per Tech Spec §6.2.3 and `patient_projection_engineering_scope.md` §5.1**,
 * the per-patient envelope encryption hierarchy is:
 *
 *     Patient Auth Factor → PMK (patient-held; never persisted at SHDT)
 *                              ↓ unwrap
 *                            KEK (per-patient; ciphertext-at-SHDT)
 *                              ↓ unwrap
 *                       Per-Record DEK (per-record; ciphertext-at-SHDT)
 *                              ↓ decrypt
 *                       Cleartext projection record (in active session memory only)
 *
 * This package ships the **isomorphic** primitive surface used by both
 * server-side test/admin paths (`@shn/phg-projection-service`) and the
 * patient session client (`@shn/phg-client-web`) per the Issue #51 Pattern B
 * ratification (audit-chain → patient-projection ingest renders + encrypts
 * client-side on session activation; SHDT never holds cleartext rendered
 * narrative).
 *
 * **Implementation: Web Crypto API (`globalThis.crypto.subtle`).**
 * Available identically in:
 *   - Modern browsers (Chrome, Safari, Firefox; iOS 14+)
 *   - React Native (`@react-native-community/crypto` polyfill or platform-
 *     native via WebView/JSI)
 *   - Node.js ≥ 20 (stable `globalThis.crypto.subtle`; falls back to
 *     `webcrypto` from `node:crypto` when needed)
 *
 * Substrate `engines.node: ">=20"` is pinned in the root package.json.
 *
 * **Doctrinal posture: NO SHDT BACKDOOR — structurally enforced.**
 *
 * `CryptoKey` objects produced by this package are `extractable: false`
 * by default everywhere the threat model permits (patient KEK in active
 * session, DEK during render+write). The Web Crypto runtime refuses to
 * surface the underlying bytes for non-extractable keys: there is no API
 * path to `JSON.stringify` them, send them across the wire, or persist
 * to localStorage / sessionStorage / IndexedDB. This is the **structural**
 * commitment: the property is enforced by the runtime, not by engineering
 * discipline (which breaks under pressure — z.coerce.boolean footgun,
 * shared-working-tree state-mixing, etc.).
 *
 * Per the `feedback_phg_client_active_session_crypto.md` memory:
 * "cleartext keys live in active session memory only; zeroize on session
 * end. Push back on framework persistence patterns." Web Crypto's
 * `extractable: false` IS that pushback at the runtime layer; framework
 * persistence patterns simply cannot serialize a non-extractable
 * CryptoKey.
 *
 * **Wrapped-bytes packed format** unchanged from the prior
 * `phg-projection-service/src/envelope-crypto.ts` shape:
 * `[iv (12 bytes) | ciphertext (variable) | tag (16 bytes)]`. AES-GCM's
 * authentication tag is appended to the ciphertext per Web Crypto's
 * standard output; this module's pack helper prepends the random IV so
 * the wrapped blob is self-describing for the unpacking path.
 *
 * **AAD discipline.** AAD bytes pass through opaquely; this module does
 * not inspect or transform them. Callers bind context (subject hash +
 * tenant + record id + key version per scope memo §5.1) at the call
 * site. AAD mismatch at decrypt/unwrap surfaces as
 * `authentication-failed`.
 *
 * **Algorithm baseline:** AES-256-GCM (per CTO direction on Issue #40,
 * matching the pre-extraction shape and Tech Spec §6.2.3). HKDF-SHA256
 * for key derivation surfaces in v0+ recovery work (Issue #48 / PFO §4
 * design; not landed here). ECDH-P256 reserved for any asymmetric
 * primitive (none should surface at v1 per Pattern B ratification).
 */
import { canonicalizeForSigning } from '@shn/audit-canonical';
// ============================================================================
// Constants
// ============================================================================
/** AES-256 key length in bytes. */
export const KEY_LENGTH_BYTES = 32;
/** AES-GCM recommended IV length in bytes (96 bits). */
export const IV_LENGTH_BYTES = 12;
/** AES-GCM authentication tag length in bytes (128 bits). */
export const TAG_LENGTH_BYTES = 16;
/**
 * Minimum length of a packed wrapped-bytes blob: IV + tag (cipher may be
 * empty for the degenerate case of an empty plaintext, which AES-GCM still
 * supports).
 */
export const MIN_WRAPPED_LENGTH_BYTES = IV_LENGTH_BYTES + TAG_LENGTH_BYTES;
// ============================================================================
// Errors
// ============================================================================
/**
 * Error thrown by envelope-crypto primitives. Discriminated by `code` so
 * callers can react programmatically; the message is for human diagnosis
 * only and does not include any cleartext.
 */
export class EnvelopeCryptoError extends Error {
    code;
    constructor(message, code) {
        super(message);
        this.name = 'EnvelopeCryptoError';
        this.code = code;
    }
}
// ============================================================================
// TypeScript narrowing for Uint8Array → BufferSource (TS 5.7+ compatibility)
// ============================================================================
/**
 * TypeScript 5.7+ narrowed `Uint8Array` to `Uint8Array<TArrayBuffer extends
 * ArrayBufferLike>` (where `ArrayBufferLike = ArrayBuffer | SharedArrayBuffer`).
 * Web Crypto's `BufferSource` accepts `ArrayBufferView<ArrayBuffer>` — i.e.,
 * NOT shared-buffer-backed views. Our `Uint8Array` instances at runtime are
 * always `ArrayBuffer`-backed (`new Uint8Array(n)` and `Uint8Array.from(...)`
 * both allocate `ArrayBuffer`), but the type system can't statically narrow
 * `Uint8Array<ArrayBufferLike>` → `Uint8Array<ArrayBuffer>` without an
 * assertion. This helper centralizes the assertion at the Web Crypto boundary.
 */
function asBufferSource(bytes) {
    return bytes;
}
// ============================================================================
// Subtle accessor (isomorphic Node 20+ / browser)
// ============================================================================
/**
 * Resolve the Web Crypto Subtle interface. Node 20+ exposes
 * `globalThis.crypto.subtle`; modern browsers do the same. Throws an
 * informative error if the runtime lacks Web Crypto (older Node, very old
 * browsers).
 */
function subtle() {
    const sub = globalThis.crypto?.subtle;
    if (sub === undefined) {
        throw new EnvelopeCryptoError('Web Crypto API is not available on this runtime. @shn/envelope-crypto requires Node ≥ 20 or a modern browser/RN runtime with globalThis.crypto.subtle.', 'invalid-key');
    }
    return sub;
}
// ============================================================================
// Key generation, import, export
// ============================================================================
/**
 * Default key usages for an AES-256-GCM CryptoKey produced by this package.
 * `encrypt` + `decrypt` for content; `wrapKey` + `unwrapKey` for envelope
 * wrapping. Callers may narrow at `generateRandomKey` / `importRawKey` /
 * `unwrapKey` time.
 */
const DEFAULT_KEY_USAGES = Object.freeze([
    'encrypt',
    'decrypt',
    'wrapKey',
    'unwrapKey',
]);
const AES_GCM_KEY_ALGORITHM = {
    name: 'AES-GCM',
    length: 256,
};
const AES_GCM_IMPORT_ALGORITHM = {
    name: 'AES-GCM',
    length: 256,
};
/**
 * Generate a fresh 256-bit AES-GCM CryptoKey from the runtime CSPRNG.
 *
 * Used for KEK provisioning at patient registration and for fresh
 * per-record DEK generation at content-write time. Per scope memo §5.1,
 * KEK and DEK generation happens client-side in the PHG application;
 * the SHDT-operated projection-service never sees cleartext key bytes
 * at rest.
 *
 * **Default `extractable: true`** because the typical use is "generate
 * a DEK, immediately wrap it under a KEK, then encrypt content under
 * the DEK." Wrapping requires the inner key to be extractable. Callers
 * generating a KEK that will be wrapped under a PMK-derived key
 * (registration path) similarly need extractability.
 *
 * For long-lived live keys that should never be wrapped or exported
 * after they exist (e.g., a session-resident KEK that has already been
 * unwrapped from its persisted ciphertext form), use
 * `unwrapKey({ extractable: false })` instead — that produces a
 * structurally-non-extractable handle.
 */
export async function generateRandomKey(opts) {
    const extractable = opts?.extractable ?? true;
    const usages = (opts?.usages ?? DEFAULT_KEY_USAGES);
    return subtle().generateKey(AES_GCM_KEY_ALGORITHM, extractable, usages);
}
/**
 * Import raw 256-bit key bytes as an AES-GCM CryptoKey.
 *
 * Used in test fixtures (deterministic test vectors), recovery paths
 * (Shamir share recombination yields raw bytes), and the rare case
 * where an external key source delivers raw bytes. Production patient-
 * session paths should prefer `generateRandomKey` (CSPRNG) or
 * `unwrapKey` (envelope unwrap) which produce structurally-bound
 * CryptoKey handles directly.
 *
 * @throws {EnvelopeCryptoError} `invalid-length` if `rawKeyBytes` is
 *         not exactly `KEY_LENGTH_BYTES` (32) bytes.
 * @throws {EnvelopeCryptoError} `invalid-key` if the bytes are
 *         all-zero (CSPRNG-returns-zeros sanity; defense-in-depth).
 */
export async function importRawKey(rawKeyBytes, opts) {
    if (rawKeyBytes.length !== KEY_LENGTH_BYTES) {
        throw new EnvelopeCryptoError(`raw key must be ${KEY_LENGTH_BYTES} bytes; received ${rawKeyBytes.length}`, 'invalid-length');
    }
    if (isAllZero(rawKeyBytes)) {
        throw new EnvelopeCryptoError('raw key must not be all-zero', 'invalid-key');
    }
    const extractable = opts?.extractable ?? false;
    const usages = (opts?.usages ?? DEFAULT_KEY_USAGES);
    return subtle().importKey('raw', asBufferSource(rawKeyBytes), AES_GCM_IMPORT_ALGORITHM, extractable, usages);
}
/**
 * Export a CryptoKey as raw bytes. Only succeeds if the key was
 * produced/imported with `extractable: true`.
 *
 * **Structural NO SHDT BACKDOOR.** If the key is `extractable: false`,
 * the runtime refuses (the underlying `subtle.exportKey` rejects); we
 * normalize to `EnvelopeCryptoError 'non-extractable'` so callers get
 * a typed signal. There is no API path to bypass this — the
 * extractability bit is enforced by the Web Crypto implementation
 * itself, not by this module.
 *
 * @throws {EnvelopeCryptoError} `non-extractable` if the key was
 *         produced with `extractable: false`.
 */
export async function exportRawKey(key) {
    if (!key.extractable) {
        throw new EnvelopeCryptoError('CryptoKey was created with extractable: false; raw bytes are structurally inaccessible (NO SHDT BACKDOOR enforcement)', 'non-extractable');
    }
    try {
        const buffer = await subtle().exportKey('raw', key);
        return new Uint8Array(buffer);
    }
    catch (cause) {
        // Belt-and-suspenders: should be unreachable since we checked
        // extractable above, but the runtime is the ultimate authority.
        throw new EnvelopeCryptoError(`exportKey failed: ${cause instanceof Error ? cause.message : String(cause)}`, 'non-extractable');
    }
}
// ============================================================================
// Core AEAD primitive
// ============================================================================
/**
 * AES-256-GCM AEAD encryption with random IV + authenticated AAD.
 *
 * Returns a packed `Uint8Array` of `[iv (12) | ciphertext (variable) |
 * tag (16)]`. Caller stores this blob; recovery requires the same
 * `key` (CryptoKey) and `aad` (bytes) used at encrypt time.
 *
 * The AAD is INTEGRITY-PROTECTED but NOT confidential. For envelope
 * encryption, AAD typically carries context that binds a key/record to
 * its scope (subject PCI hash + tenant ID + record ID + key version
 * per scope memo §5.1). Callers MUST pass the same AAD at decrypt;
 * mismatch triggers `authentication-failed`.
 *
 * @throws {EnvelopeCryptoError} `invalid-key` if `key.algorithm.name`
 *         is not `'AES-GCM'` or the key lacks `'encrypt'` usage.
 */
export async function aeadEncrypt(plaintext, key, aad) {
    assertAesGcmKey(key, 'encrypt');
    const iv = randomIv();
    const ciphertextWithTag = await subtle().encrypt({
        name: 'AES-GCM',
        iv: asBufferSource(iv),
        additionalData: asBufferSource(aad),
        tagLength: TAG_LENGTH_BYTES * 8,
    }, key, asBufferSource(plaintext));
    return packWrapped(iv, new Uint8Array(ciphertextWithTag));
}
/**
 * AES-256-GCM AEAD decryption. Counterpart to `aeadEncrypt`.
 *
 * Verifies the authentication tag against the provided `key` and `aad`
 * before returning plaintext. A tag mismatch — caused by ciphertext
 * tampering, wrong key, or AAD mismatch — throws
 * `authentication-failed`. The error message intentionally does not
 * distinguish cause, to avoid leaking which input was incorrect.
 *
 * @throws {EnvelopeCryptoError} `invalid-length` if the wrapped blob
 *         is shorter than IV + tag minimum.
 * @throws {EnvelopeCryptoError} `invalid-key` if the key is unsuitable
 *         for AES-GCM decryption.
 * @throws {EnvelopeCryptoError} `authentication-failed` if tag
 *         verification fails.
 */
export async function aeadDecrypt(wrapped, key, aad) {
    assertAesGcmKey(key, 'decrypt');
    if (wrapped.length < MIN_WRAPPED_LENGTH_BYTES) {
        throw new EnvelopeCryptoError(`wrapped blob too short: minimum ${MIN_WRAPPED_LENGTH_BYTES} bytes (IV + tag); received ${wrapped.length}`, 'invalid-length');
    }
    const { iv, ciphertextWithTag } = unpackWrapped(wrapped);
    try {
        const plaintext = await subtle().decrypt({
            name: 'AES-GCM',
            iv: asBufferSource(iv),
            additionalData: asBufferSource(aad),
            tagLength: TAG_LENGTH_BYTES * 8,
        }, key, asBufferSource(ciphertextWithTag));
        return new Uint8Array(plaintext);
    }
    catch {
        // Web Crypto throws an opaque OperationError on tag verification
        // failure; normalize to our typed error code. Do NOT include
        // cleartext or key material in the rethrown message.
        throw new EnvelopeCryptoError('AEAD authentication failed: ciphertext, key, or AAD does not match', 'authentication-failed');
    }
}
// ============================================================================
// Key wrapping (envelope-encryption layer)
// ============================================================================
/**
 * Wrap an inner CryptoKey under a wrapping CryptoKey. Used for:
 *
 *   - KEK ← PMK (PHG client at registration; service stores wrapped
 *     KEK ciphertext)
 *   - DEK ← KEK (PHG client at record-write time; service stores
 *     wrapped DEK ciphertext alongside the projection record)
 *
 * Web Crypto's `wrapKey` requires `innerKey.extractable === true` —
 * the wrap operation conceptually exposes the inner key bytes (under
 * encryption) for transport. Callers generating a fresh DEK to be
 * wrapped should pass `generateRandomKey({ extractable: true })`.
 *
 * The AAD must bind the wrap context (subject hash || tenant || record
 * id || kek version etc., per the orchestration AAD scheme). AAD
 * mismatch at unwrap time triggers `authentication-failed`.
 *
 * @throws {EnvelopeCryptoError} `invalid-key` if `wrappingKey` lacks
 *         `'wrapKey'` usage or `innerKey` is non-extractable.
 */
export async function wrapKey(innerKey, wrappingKey, aad) {
    if (!innerKey.extractable) {
        throw new EnvelopeCryptoError('innerKey must be extractable: true to be wrapped (Web Crypto requirement)', 'invalid-key');
    }
    assertAesGcmKey(wrappingKey, 'wrapKey');
    const iv = randomIv();
    const wrappedBytes = await subtle().wrapKey('raw', innerKey, wrappingKey, {
        name: 'AES-GCM',
        iv: asBufferSource(iv),
        additionalData: asBufferSource(aad),
        tagLength: TAG_LENGTH_BYTES * 8,
    });
    return packWrapped(iv, new Uint8Array(wrappedBytes));
}
/**
 * Unwrap a wrapped CryptoKey produced by `wrapKey`.
 *
 * Returns a CryptoKey suitable for further use; the runtime keeps the
 * underlying bytes opaque. **Default `extractable: false`** — the
 * unwrapped key is structurally non-exportable from this point on,
 * enforcing the NO SHDT BACKDOOR posture for downstream operations.
 *
 * Callers that need an extractable unwrap (e.g., recovery paths that
 * re-wrap under a fresh KEK) explicitly opt in via
 * `unwrapKey(..., { extractable: true })`.
 *
 * @throws {EnvelopeCryptoError} `invalid-length` if the wrapped blob
 *         is shorter than IV + tag minimum.
 * @throws {EnvelopeCryptoError} `invalid-key` if `wrappingKey` lacks
 *         `'unwrapKey'` usage.
 * @throws {EnvelopeCryptoError} `authentication-failed` if tag
 *         verification fails (ciphertext, wrappingKey, or AAD mismatch).
 */
export async function unwrapKey(wrappedBytes, wrappingKey, aad, opts) {
    if (wrappedBytes.length < MIN_WRAPPED_LENGTH_BYTES) {
        throw new EnvelopeCryptoError(`wrapped blob too short: minimum ${MIN_WRAPPED_LENGTH_BYTES} bytes (IV + tag); received ${wrappedBytes.length}`, 'invalid-length');
    }
    assertAesGcmKey(wrappingKey, 'unwrapKey');
    const { iv, ciphertextWithTag } = unpackWrapped(wrappedBytes);
    const extractable = opts?.extractable ?? false;
    const usages = (opts?.usages ?? DEFAULT_KEY_USAGES);
    try {
        return await subtle().unwrapKey('raw', asBufferSource(ciphertextWithTag), wrappingKey, {
            name: 'AES-GCM',
            iv: asBufferSource(iv),
            additionalData: asBufferSource(aad),
            tagLength: TAG_LENGTH_BYTES * 8,
        }, AES_GCM_IMPORT_ALGORITHM, extractable, usages);
    }
    catch {
        throw new EnvelopeCryptoError('AEAD authentication failed: ciphertext, key, or AAD does not match', 'authentication-failed');
    }
}
// ============================================================================
// Record encryption (data layer)
// ============================================================================
/**
 * Encrypt a patient-projection record's cleartext content under a DEK.
 *
 * Direct call to `aeadEncrypt`; the named function makes call sites
 * read more clearly (record encryption is the data-layer use of the
 * AEAD primitive; key wrapping is the envelope-layer use). Both layers
 * use AES-256-GCM with the same packed wrapping format.
 *
 * AAD typically binds: `subjectPciHash || tenantId || recordId ||
 * kekId || dekId` per the orchestration AAD scheme.
 */
export async function encryptRecord(plaintext, dek, aad) {
    return aeadEncrypt(plaintext, dek, aad);
}
/**
 * Decrypt a patient-projection record's ciphertext under an unwrapped DEK.
 * Counterpart to `encryptRecord`.
 *
 * @throws {EnvelopeCryptoError} `authentication-failed` if ciphertext,
 *         DEK, or AAD does not match.
 */
export async function decryptRecord(ciphertext, dek, aad) {
    return aeadDecrypt(ciphertext, dek, aad);
}
// ============================================================================
// Memory-hygiene helpers
// ============================================================================
/**
 * Zero a raw byte buffer in place. Used by callers after they've
 * consumed transient cleartext bytes (e.g., raw key material received
 * from a recovery share-recombine, before/after `importRawKey`).
 *
 * **Caveat — JavaScript memory hygiene is approximate.** JS engines
 * may copy buffer contents during garbage collection, leaving stale
 * cleartext in heap regions that this function cannot reach. For the
 * patient-fiduciary doctrine's threat model, the load-bearing
 * protection is `extractable: false` on `CryptoKey` objects (which
 * never expose underlying bytes); raw-bytes zeroize is defense-in-
 * depth for the narrow case where raw bytes pass through caller code.
 *
 * **`CryptoKey` objects DO NOT need zeroize** — the runtime manages
 * their lifecycle, and non-extractable keys never expose raw bytes.
 * Discard the reference and let GC reclaim it.
 */
export function zeroizeBytes(buf) {
    buf.fill(0);
}
// ============================================================================
// Passphrase-based key derivation (PBKDF2-SHA-256)
// ============================================================================
/**
 * OWASP-2023 recommended PBKDF2-SHA-256 iteration count for password-based
 * derivation. Tunable via `DeriveKeyFromPassphraseOptions.iterations` for
 * tests and future tuning per ADR-coordinated security review. Production
 * callers should NOT lower below this without an explicit security-comp-lead
 * sign-off (when that hire lands).
 *
 * Per `feedback_phg_recovery_ux_honesty.md` discipline: derivation cost is
 * a load-bearing protection against offline-passphrase-cracking adversary
 * (the named threat in the AWS Stage 1+ S3 read-access scenario).
 */
export const DEFAULT_PBKDF2_ITERATIONS = 600_000;
/**
 * Default key usages for a CryptoKey derived from a passphrase. The derived
 * key is intended to wrap or unwrap a higher-level key (typically a wrapped
 * KEK persisted at SHDT); usages are narrowed to the wrap/unwrap surface.
 *
 * Callers may further narrow at derivation time via
 * `DeriveKeyFromPassphraseOptions.usages` — e.g., recovery-passphrase
 * derivation that ONLY unwraps a wrapped-PMK passes `['unwrapKey']`.
 */
const DEFAULT_DERIVATION_USAGES = Object.freeze([
    'wrapKey',
    'unwrapKey',
]);
/**
 * Derive an AES-256-GCM `CryptoKey` from a patient passphrase + salt via
 * PBKDF2-SHA-256.
 *
 * **Returned key is `extractable: false`.** The runtime refuses to surface
 * the derived bytes per the structural NO SHDT BACKDOOR posture (Tech Spec
 * §6.2.3 + scope memo §5.3 + memory `feedback_phg_client_active_session_crypto`).
 * Patient sessions hold the derived key only as a CryptoKey reference and
 * use it to unwrap higher-level keys; the derived bytes never reach
 * JavaScript-accessible memory.
 *
 * **Why PBKDF2 over Argon2id.** PBKDF2 is the only password-based KDF
 * available in Web Crypto without a polyfill (Argon2id requires a library
 * ship). v1 commits to PBKDF2-SHA-256 with 600,000 iterations per OWASP
 * 2023 guidance; v1.5+ may add Argon2id via a separate coord-issue
 * ratification if the threat model demands. Iteration count is tunable
 * for tests + future tuning per `DeriveKeyFromPassphraseOptions.iterations`.
 *
 * **Cleartext passphrase hygiene.** The passphrase string is encoded to
 * UTF-8 bytes inside this function; the bytes are zeroized after the
 * `importKey` step as defense-in-depth. The load-bearing protection is
 * the non-extractable derived CryptoKey; raw-bytes zeroize covers the
 * narrow case where bytes pass through caller-visible buffers. Per the
 * `zeroizeBytes` JSDoc caveat: JS memory hygiene is approximate (interned
 * strings may survive in heap regions this function cannot reach); the
 * structural protection is what the threat model relies on.
 *
 * **Use cases:**
 *
 *   - **Patient master key (PMK) derivation** at session activation:
 *     `salt = patient registration salt`, `usages = ['wrapKey', 'unwrapKey']`.
 *     Derived PMK unwraps the wrapped KEK persisted at SHDT.
 *   - **Recovery-passphrase derivation** at recovery flow per scope memo
 *     §5.2: `salt = recovery-passphrase salt enrolled at registration`
 *     (distinct from primary auth salt), `usages = ['unwrapKey']`. Derived
 *     key unwraps the wrapped-PMK reconstructed from threshold shares.
 *
 * Both surfaces share the same canonical primitive — drift between the
 * two surfaces would surface as `authentication-failed` at unwrap time
 * across the boundary.
 *
 * @throws {EnvelopeCryptoError} `'invalid-length'` if passphrase or salt
 *         is empty.
 * @throws {EnvelopeCryptoError} `'derivation-failed'` if Web Crypto's
 *         PBKDF2 / deriveKey fails for runtime reasons (typically
 *         SubtleCrypto unavailability or invalid input).
 */
export async function deriveKeyFromPassphrase(opts) {
    if (opts.passphrase.length === 0) {
        throw new EnvelopeCryptoError('passphrase must be non-empty', 'invalid-length');
    }
    if (opts.salt.length === 0) {
        throw new EnvelopeCryptoError('salt must be non-empty', 'invalid-length');
    }
    const sub = subtle();
    const iterations = opts.iterations ?? DEFAULT_PBKDF2_ITERATIONS;
    const usages = opts.usages ?? DEFAULT_DERIVATION_USAGES;
    // Encode passphrase as UTF-8. Cleartext bytes exist transiently in the
    // encoded buffer for the duration of the importKey call; zeroize after.
    const passwordBytes = new TextEncoder().encode(opts.passphrase);
    let pbkdf2Key;
    try {
        pbkdf2Key = await sub.importKey('raw', asBufferSource(passwordBytes), 'PBKDF2', false, ['deriveKey']);
    }
    finally {
        // Defense-in-depth: zero the password bytes. Load-bearing protection
        // is the non-extractable derived CryptoKey produced below.
        zeroizeBytes(passwordBytes);
    }
    try {
        return await sub.deriveKey({
            name: 'PBKDF2',
            salt: asBufferSource(opts.salt),
            iterations,
            hash: 'SHA-256',
        }, pbkdf2Key, { name: 'AES-GCM', length: 256 }, 
        // Derived key is non-extractable per the doctrinal posture.
        false, 
        // Spread to a mutable array — Web Crypto types want KeyUsage[] not ReadonlyArray.
        [...usages]);
    }
    catch (e) {
        throw new EnvelopeCryptoError(`passphrase-based key derivation failed: ${e instanceof Error ? e.message : String(e)}`, 'derivation-failed');
    }
}
/**
 * Derive the combined unwrap key for the recovery-passphrase wrapped-PMK
 * envelope per Reading C ratification. Returns a non-extractable
 * AES-256-GCM `CryptoKey` with `unwrapKey` usage; client passes to
 * `unwrapKey(wrappedPmkCiphertext, combinedKey, aad)` to recover the
 * PMK in active-session memory.
 *
 * **Web Crypto HKDF-SHA-256** is the canonical KDF combine algorithm.
 * Sec/comp-lead-on-hire amendment for HKDF-SHA-512 + algorithm-suite
 * versioning per `envelopeVersion` axis (memo §6 Argon2id deferral).
 *
 * @throws {EnvelopeCryptoError} `'invalid-length'` if any required
 *         input is empty.
 * @throws {EnvelopeCryptoError} `'derivation-failed'` if Web Crypto's
 *         PBKDF2 / HKDF / deriveKey fails for runtime reasons.
 */
export async function deriveCombinedUnwrapKey(opts) {
    if (opts.thresholdMaterial.length === 0) {
        throw new EnvelopeCryptoError('thresholdMaterial must be non-empty', 'invalid-length');
    }
    if (opts.passphrase.length === 0) {
        throw new EnvelopeCryptoError('passphrase must be non-empty', 'invalid-length');
    }
    if (opts.salt.length === 0) {
        throw new EnvelopeCryptoError('salt must be non-empty', 'invalid-length');
    }
    if (opts.transcript.length === 0) {
        throw new EnvelopeCryptoError('transcript must be non-empty', 'invalid-length');
    }
    const sub = subtle();
    const iterations = opts.iterations ?? DEFAULT_PBKDF2_ITERATIONS;
    // Step 1: PBKDF2-SHA-256 → 32-byte passphrase intermediate (raw bits).
    const passwordBytes = new TextEncoder().encode(opts.passphrase);
    let passphraseIntermediate;
    try {
        let pbkdf2BaseKey;
        try {
            pbkdf2BaseKey = await sub.importKey('raw', asBufferSource(passwordBytes), 'PBKDF2', false, ['deriveBits']);
        }
        finally {
            zeroizeBytes(passwordBytes);
        }
        const pbkdf2Bits = await sub.deriveBits({
            name: 'PBKDF2',
            salt: asBufferSource(opts.salt),
            iterations,
            hash: 'SHA-256',
        }, pbkdf2BaseKey, 256);
        passphraseIntermediate = new Uint8Array(pbkdf2Bits);
    }
    catch (e) {
        throw new EnvelopeCryptoError(`passphrase-derivation step failed: ${e instanceof Error ? e.message : String(e)}`, 'derivation-failed');
    }
    // Step 2: IKM = passphraseIntermediate ‖ thresholdMaterial.
    const ikm = new Uint8Array(passphraseIntermediate.length + opts.thresholdMaterial.length);
    ikm.set(passphraseIntermediate, 0);
    ikm.set(opts.thresholdMaterial, passphraseIntermediate.length);
    try {
        // Step 3: import IKM as HKDF base (non-extractable; usage 'deriveKey').
        const hkdfBase = await sub.importKey('raw', asBufferSource(ikm), 'HKDF', false, ['deriveKey']);
        // Step 4: HKDF-SHA-256.deriveKey(salt=∅, info=transcript) → AES-256-GCM
        // unwrapKey CryptoKey (non-extractable).
        return await sub.deriveKey({
            name: 'HKDF',
            salt: new Uint8Array(),
            info: asBufferSource(opts.transcript),
            hash: 'SHA-256',
        }, hkdfBase, { name: 'AES-GCM', length: 256 }, false, ['unwrapKey']);
    }
    catch (e) {
        throw new EnvelopeCryptoError(`HKDF-combine step failed: ${e instanceof Error ? e.message : String(e)}`, 'derivation-failed');
    }
    finally {
        // Step 5: zeroize passphraseIntermediate + IKM bytes (defense-in-depth).
        zeroizeBytes(passphraseIntermediate);
        zeroizeBytes(ikm);
    }
}
/**
 * GF(2^8) byte-wise Shamir SSS valid share-index range. Same constants as
 * `services/phg-projection-service/src/recovery/share-aad-encoder.ts`
 * (PR #364) + `clients/phg-client-web/src/recovery/share-aad-encoder.ts`
 * client mirror. Memo §2.3 v0.3 byte-wise Shamir SSS amendment.
 */
export const LAYER_EVIDENCE_SHARE_INDEX_MIN = 1;
export const LAYER_EVIDENCE_SHARE_INDEX_MAX = 255;
/**
 * Encode the canonical HKDF info parameter bytes for layer-evidence-key
 * derivation. Returns RFC 8785 JCS UTF-8 bytes via
 * `@shn/audit-canonical.canonicalizeForSigning`.
 *
 * Validates field-value invariants (canonical layer-kind literal; non-empty
 * strings; integer share index in GF(2^8) nonzero range) before
 * canonicalization.
 *
 * Co-located with `deriveLayerEvidenceKey` in the shared `@shn/envelope-
 * crypto` package per `recovery_flow_layer_evidence_key_derivation_design.md`
 * §11.3 implementation-time disposition (co-located shared canonical encoder
 * — both client and server import the same primitive; structural drift-free
 * by construction since there is one source of truth, not parallel copies
 * needing parallel binding tests). Differs from `encodeRecoveryAad`
 * (PR #323 + PR #326 mirror) + `encodeShareAad` (PR #364 server + client
 * mirror in single PR) precedent because those encoders live in service-only
 * packages with NodeJS-only AWS SDK transitive deps; `@shn/envelope-crypto`
 * is shared (browser + Node 20+) so co-location is structurally correct.
 *
 * @throws {EnvelopeCryptoError} `'invalid-length'` if any field invariant
 *   is violated (layer-kind not in canonical literal union; empty
 *   subjectPciHash or recoveryEnvelopeId; non-integer or out-of-range
 *   shareIndex).
 */
export function encodeLayerEvidenceKeyInfo(fields) {
    if (fields.layerKind !== 'knowledge' &&
        fields.layerKind !== 'possession' &&
        fields.layerKind !== 'ial2-reverify' &&
        fields.layerKind !== 'in-person' &&
        fields.layerKind !== 'trusted-other') {
        throw new EnvelopeCryptoError(`layerKind '${String(fields.layerKind)}' is not a canonical LayerKind literal`, 'invalid-length');
    }
    if (fields.subjectPciHash.length === 0) {
        throw new EnvelopeCryptoError('subjectPciHash must be non-empty', 'invalid-length');
    }
    if (fields.recoveryEnvelopeId.length === 0) {
        throw new EnvelopeCryptoError('recoveryEnvelopeId must be non-empty', 'invalid-length');
    }
    if (!Number.isInteger(fields.shareIndex)) {
        throw new EnvelopeCryptoError('shareIndex must be an integer', 'invalid-length');
    }
    if (fields.shareIndex < LAYER_EVIDENCE_SHARE_INDEX_MIN ||
        fields.shareIndex > LAYER_EVIDENCE_SHARE_INDEX_MAX) {
        throw new EnvelopeCryptoError(`shareIndex must be in [${LAYER_EVIDENCE_SHARE_INDEX_MIN}, ${LAYER_EVIDENCE_SHARE_INDEX_MAX}] ` +
            `(GF(2^8) nonzero element space per byte-wise Shamir SSS)`, 'invalid-length');
    }
    return canonicalizeForSigning({
        layerKind: fields.layerKind,
        recoveryEnvelopeId: fields.recoveryEnvelopeId,
        shareIndex: fields.shareIndex,
        subjectPciHash: fields.subjectPciHash,
    });
}
/**
 * v1 minimum entropy floor for evidence material (in bytes). 16 bytes is
 * the v1 lower bound per
 * `recovery_flow_layer_evidence_key_derivation_design.md` §3 (sec/comp-
 * lead-on-hire amendment marker preserved per substrate-state-warrant
 * registry §3 reframe at parameter-set tuning).
 */
export const LAYER_EVIDENCE_MIN_BYTES = 16;
/**
 * Derive a per-share, per-layer-kind AEAD unwrap key for the Layer 1
 * per-share AEAD wrap surface (Step 3b memo §2.2 v0.2 amendment).
 *
 * HKDF-SHA-256 over evidence material with layer-kind-tagged info parameter.
 * Per [DEV1 axis-3 architectural disposition at PR #339 issuecomment-
 * 4406475650](https://github.com/SmartHealthNetwork/SHN2026/pull/339#issuecomment-4406475650)
 * option (a) ratified;
 * `recovery_flow_layer_evidence_key_derivation_design.md` §3 (memo merged
 * at [PR #376](https://github.com/SmartHealthNetwork/SHN2026/pull/376)).
 *
 * Internal chain:
 *   HKDF-SHA-256.deriveKey(salt=∅, info=info, IKM=evidenceMaterial)
 *     → AES-256-GCM CryptoKey with `['encrypt', 'decrypt']` usages; non-
 *     extractable. The derived key is used with `aeadEncrypt` / `aeadDecrypt`
 *     to wrap / unwrap Shamir share BYTE material (per memo §2.2 v0.2
 *     amendment: `recoveryShareCiphertext_i = AEAD-AES-256-GCM(ShamirShare_i,
 *     layerEvidenceKey_i, shareAad_i)` — the wrapped material is bytes, not
 *     a CryptoKey, so `aeadEncrypt`/`aeadDecrypt` is the correct primitive
 *     pair, not `wrapKey`/`unwrapKey`).
 *
 * Active-session-only crypto preserved per
 * `feedback_phg_client_active_session_crypto.md` + ADR-032 §1: derived key
 * is non-extractable AES-GCM CryptoKey; transient IKM (evidenceMaterial)
 * IS NOT zeroized by this primitive (caller-owned buffer; caller
 * responsible for zeroization on completion per scope memo §5.3).
 *
 * @throws {EnvelopeCryptoError} `'invalid-length'` if `evidenceMaterial`
 *   is below `LAYER_EVIDENCE_MIN_BYTES` (16 bytes minimum) or `info` is
 *   empty.
 * @throws {EnvelopeCryptoError} `'derivation-failed'` if Web Crypto's
 *   HKDF importKey/deriveKey fails for runtime reasons.
 */
export async function deriveLayerEvidenceKey(opts) {
    if (opts.evidenceMaterial.length < LAYER_EVIDENCE_MIN_BYTES) {
        throw new EnvelopeCryptoError(`evidenceMaterial must be at least ${LAYER_EVIDENCE_MIN_BYTES} bytes ` +
            `(received ${opts.evidenceMaterial.length}); v1 entropy floor`, 'invalid-length');
    }
    if (opts.info.length === 0) {
        throw new EnvelopeCryptoError('info must be non-empty', 'invalid-length');
    }
    const sub = subtle();
    try {
        const hkdfBase = await sub.importKey('raw', asBufferSource(opts.evidenceMaterial), 'HKDF', false, ['deriveKey']);
        return await sub.deriveKey({
            name: 'HKDF',
            salt: new Uint8Array(),
            info: asBufferSource(opts.info),
            hash: 'SHA-256',
        }, hkdfBase, { name: 'AES-GCM', length: 256 }, false, ['encrypt', 'decrypt']);
    }
    catch (e) {
        throw new EnvelopeCryptoError(`HKDF derivation step failed: ${e instanceof Error ? e.message : String(e)}`, 'derivation-failed');
    }
}
/**
 * Constant-time byte equality check. Used when comparing key
 * fingerprints, AAD-derived identifiers, or other secret-adjacent
 * material where timing leaks would matter.
 *
 * Implementation uses an XOR-accumulator pattern; runtime is `O(min(a.length,
 * b.length))`. Portable across browser and Node.
 */
export function constantTimeEqual(a, b) {
    if (a.length !== b.length)
        return false;
    let acc = 0;
    for (let i = 0; i < a.length; i++) {
        acc |= a[i] ^ b[i];
    }
    return acc === 0;
}
// ============================================================================
// Internal helpers
// ============================================================================
function randomIv() {
    const iv = new Uint8Array(IV_LENGTH_BYTES);
    globalThis.crypto.getRandomValues(iv);
    return iv;
}
function packWrapped(iv, ciphertextWithTag) {
    const out = new Uint8Array(iv.length + ciphertextWithTag.length);
    out.set(iv, 0);
    out.set(ciphertextWithTag, iv.length);
    return out;
}
function unpackWrapped(wrapped) {
    return {
        iv: wrapped.slice(0, IV_LENGTH_BYTES),
        ciphertextWithTag: wrapped.slice(IV_LENGTH_BYTES),
    };
}
function isAllZero(buf) {
    let acc = 0;
    for (let i = 0; i < buf.length; i++) {
        acc |= buf[i];
    }
    return acc === 0;
}
function assertAesGcmKey(key, requiredUsage) {
    if (key.algorithm.name !== 'AES-GCM') {
        throw new EnvelopeCryptoError(`key algorithm must be AES-GCM; received ${key.algorithm.name}`, 'invalid-key');
    }
    if (!key.usages.includes(requiredUsage)) {
        throw new EnvelopeCryptoError(`key lacks '${requiredUsage}' usage; usages: [${key.usages.join(', ')}]`, 'invalid-key');
    }
}
//# sourceMappingURL=index.js.map